import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash-es';

import { useAppSelector } from '@/store';
import { selectNShowcase } from '@/store/slices/userPreferences';

import { PageShell } from '../../components';
import { ROUTE } from '../../routes';
import { useMockEndpoint } from '../../hooks/useMockEndpoint';
import { useShowInProgress } from '../../hooks/useShowInProgress';
import { buildAcquisitionData, ACQ_BRAND_VARIANTS, type AcqBrandVariant } from '../../mock-data/acquisition';
import { customDays, customPeriodLabel } from '../home/stats-mapping';
import { useStudioPeriod } from '../home/useStudioPeriod';
import { stepPresetOf } from './performance-stats';
import { useAcquisitionBudgetOverride } from './useAcquisitionBudget';
import { useAcquisitionCampaigns } from './useAcquisitionCampaigns';
import { useAcquisitionChannels } from './useAcquisitionChannels';
import { useAcquisitionPerformance } from './useAcquisitionPerformance';
import { useAffiliateProgress } from './useAffiliateProgress';
import { useAcquisitionChannelGate } from '../../hooks/useAcquisitionChannelGate';
import { Hero, ChannelsBudgets, SelfManaged, Campaigns, Performance } from './components';
import type { CampaignsHandle } from './components/Campaigns/Campaigns';
import { AcqPage } from './acquisition.styles';

export type AcquisitionProps = {
  styleText?: string;
  className?: string;
};

/**
 * Acquisition root page — a pixel-faithful rebuild of the source dashboard
 * (hero ring gauge + AI actions, Lumio channels + budgets, self-managed
 * channels, the campaigns browser, and the KPI performance grid). The Lumio
 * channels + budgets and the campaigns browser show only under the plain "777"
 * reveal; without it the hero scores the affiliate program (`affiliate-score.ts`).
 */
const Acquisition = ({ styleText = '', className = '' }: AcquisitionProps): React.ReactElement => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // The Lumio Channels + Budgets card and the Campaigns browser show only under the plain "777" reveal
  // (`show_in_progress`), live on a flip, and their reads run only then. Without it the hero scores the affiliate
  // program instead of the Lumio levers, and its one action opens Affiliates.
  const showLumioSections = useShowInProgress();
  const { isEnabled: isChannelEnabled } = useAcquisitionChannelGate();

  // Performance Overview range, same options as Home/Stats; drives BOTH tile columns
  // (left = selected range, right = the "next step up" range). The range the owner last
  // picked on any page with one, Today until then.
  const [perfRange, setPerfRange] = useStudioPeriod('TODAY');

  // Per-brand mock (no brand-scoped acquisition API yet): map the displayed brand
  // to one of the mock datasets by its list position, cycling — so the hero gauge
  // walks through different score states as the brand switcher changes. Read
  // `displayBrand` (the lagged content brand promoted under the BrandTransition
  // fade), not `activeBrand`, so the previous brand's data stays on screen through
  // the fade-out — same pattern as the Home dashboard. Replace with a real
  // per-brand selector when the metrics API exists.
  const brands = useAppSelector((state) => state.casinoInstance.brands, isEqual);
  const displayBrand = useAppSelector((state) => state.casinoInstance.displayBrand, isEqual);
  const variant = React.useMemo<AcqBrandVariant>(() => {
    if (!displayBrand) return 0;
    const idx = brands.findIndex((brand) => brand.id === displayBrand.id);
    return (Math.max(0, idx) % ACQ_BRAND_VARIANTS.length) as AcqBrandVariant;
  }, [brands, displayBrand]);

  // Marketing budget (wallet + daily cap) comes from the acquisition-budget API,
  // keyed by the displayed brand, and feeds BOTH the budget half of the hero score
  // and the budget card — so the gauge and the card always read the same real
  // numbers. Until it loads, `budgetsReady` is false and the mock profile's budget
  // drives them; the queued-suggestion count (campaign half) stays mock. The modal
  // (`AdBudgetLimit`) reads the same slice, so card + modal agree too.
  const { override: budgetOverride, ready: budgetsReady } = useAcquisitionBudgetOverride(displayBrand?.id);

  // The campaigns browser pool is wired to the Casino Ad Service (per active brand);
  // the tabs/filters chrome stays static, and the browser derives its counts from
  // this pool. Only live campaigns (Running / Paused) are in it: the ones the service
  // is still building, or never shows, are dropped by the mapper.
  const campaigns = useAcquisitionCampaigns(showLumioSections ? displayBrand?.tenant_id : undefined);

  // The affiliate program: the real count for the Affiliates self-managed card (still
  // resolving -> undefined so the card keeps its placeholder; not provisioned -> 0; the
  // probe never provisions), and, outside the reveal, the steps the hero scores.
  // Affiliates are per-owner (not brand-scoped on the API), so the count matches the
  // Affiliates page.
  const affiliateProgress = useAffiliateProgress(displayBrand?.id, !showLumioSections);
  const affiliateCount = affiliateProgress.affiliateCount;

  // The score's campaign half measures a BACKLOG: suggestions sitting there waiting
  // for the owner to act. The Ad Service yields none (it reports campaigns that were
  // already created, and the mapper shows only the live ones), so the queue is empty
  // and the campaign half scores full marks until a real suggestions source (Lumio)
  // is wired. Home feeds the same 0 into the same score, and must keep matching.
  const suggestedCount = 0;

  const data = React.useMemo(
    () => buildAcquisitionData(variant, budgetOverride, suggestedCount),
    [variant, budgetOverride, suggestedCount],
  );
  const budgets = data.budgets;

  // Drive the Affiliates card's count from the live affiliate count (the Influencers entrypoint
  // was removed from the mock source). Always override with `affiliateCount`, which is `undefined`
  // while it resolves so the card shows a placeholder rather than the mock's misleading 0.
  const selfManagedChannels = React.useMemo(
    () =>
      data.selfManagedChannels.channels.map((channel) =>
        channel.id === 'affiliates' ? { ...channel, count: affiliateCount } : channel,
      ),
    [data.selfManagedChannels.channels, affiliateCount],
  );

  // One endpoint per data-backed section: each loads on its own randomised delay
  // (so the page fills in unevenly), while every figure within a section shares
  // that one wait. Swap each `useMockEndpoint` for a real query hook of the same
  // `{ data, loading }` shape later — the sections don't change. The Self-managed
  // and Campaigns browsers are navigation/list content (no figures), so they stay
  // un-gated.
  const insights = useMockEndpoint(data.insights);
  // The "Lumio Channels" cards are wired for the LIVE channels (only Web today): real
  // spend (Ad Service) / daily budget (acquisition-budget) / CPA (cost ÷ FTDs from Stats).
  // Locked channels keep their frosted mock figures. Same numbers as the channel-detail page.
  const lumioChannels = useAcquisitionChannels(
    showLumioSections ? displayBrand?.tenant_id : undefined,
    showLumioSections ? displayBrand?.id : undefined,
    data.lumioChannels.channels,
  );
  // The Performance grid is wired to the Stats API, harmonised with Home/Stats: the
  // selected range drives the left column and its "next step up" drives the right (see
  // the hook). The three ad-spend KPIs are sourced from the Casino Ad Service spend,
  // keyed by the brand's tenant id.
  const performance = useAcquisitionPerformance(
    displayBrand?.id,
    displayBrand?.tenant_id,
    data.performanceOverview.kpis,
    perfRange,
  );

  // Per-column heading + delta caption for the selected range, translated at the render
  // site (literal `t()` per branch so the extractor harvests them and they re-translate
  // on a language switch). Left = the selected range; right = the "next step up".
  const perfLabels = React.useMemo(() => {
    const leftLabel = ((): string => {
      switch (perfRange) {
        case 'TODAY':
          return t('Today');
        case 'YESTERDAY':
          return t('Yesterday');
        case '7D':
          return t('Last 7 days');
        case '30D':
          return t('Last 30 days');
        case '90D':
          return t('Last 90 days');
        case '6M':
          return t('Last 6 months');
        case '1Y':
          return t('Last year');
        default:
          return customPeriodLabel(perfRange, i18n.language) ?? t('Last 7 days');
      }
    })();
    const leftCaption = ((): string => {
      switch (perfRange) {
        case 'TODAY':
          return t('vs yesterday');
        case 'YESTERDAY':
          return t('vs previous day');
        case '7D':
          return t('vs previous 7 days');
        case '30D':
          return t('vs previous 30 days');
        case '90D':
          return t('vs previous 90 days');
        case '6M':
          return t('vs previous 6 months');
        case '1Y':
          return t('vs previous year');
        default:
          // A custom range compares with the same number of days just before it.
          return t('vs the {{n}} days before', { n: customDays(perfRange)?.days ?? 7 });
      }
    })();
    // A custom range steps up like the shortest preset at least as long (see `stepPresetOf`).
    const stepPreset = stepPresetOf(perfRange);
    const rightLabel = ((): string => {
      switch (stepPreset) {
        case 'TODAY':
        case 'YESTERDAY':
          return t('This week');
        case '7D':
          return t('Last 30 days');
        case '30D':
          return t('Last 60 days');
        case '90D':
          return t('Last 6 months');
        case '6M':
          return t('Last year');
        case '1Y':
          return t('Last 2 years');
      }
    })();
    const rightCaption = ((): string => {
      switch (stepPreset) {
        case 'TODAY':
        case 'YESTERDAY':
          return t('vs last week');
        case '7D':
          return t('vs previous 30 days');
        case '30D':
          return t('vs previous 60 days');
        case '90D':
          return t('vs previous 6 months');
        case '6M':
          return t('vs previous year');
        case '1Y':
          return t('vs previous 2 years');
      }
    })();
    return { leftLabel, leftCaption, rightLabel, rightCaption };
  }, [perfRange, t, i18n.language]);

  const campaignsRef = React.useRef<CampaignsHandle>(null);
  const [openBudgetDialog, setOpenBudgetDialog] = React.useState(false);
  // The dialog lives in the Lumio card: hiding the card closes it, so turning the reveal back on never reopens it.
  if (!showLumioSections && openBudgetDialog) setOpenBudgetDialog(false);
  const onOpenBudgetLimits = React.useCallback(() => setOpenBudgetDialog(true), []);
  const onCloseBudgetLimits = React.useCallback(() => setOpenBudgetDialog(false), []);

  // The hero's AI suggestions are shortcuts into the page: "Increase daily budget"
  // opens the Budget & Limits dialog (which lands on its Daily Limits tab), and
  // "Activate a new campaign" jumps to the Suggested tab of the campaigns browser.
  const onHeroAction = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const id = event.currentTarget.dataset.id;
      if (id === 'increase-daily-budget') onOpenBudgetLimits();
      else if (id === 'activate-new-campaign') campaignsRef.current?.revealSuggestions();
      else if (id === 'open-affiliates') navigate(ROUTE.affiliates());
      else console.log('oh.module.acquisition[heroAction]', id);
    },
    [onOpenBudgetLimits, navigate],
  );

  const onOpenChannel = React.useCallback(
    (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
      const id = event.currentTarget.dataset.id;
      if (id) navigate(ROUTE.channelDetail(id));
    },
    [navigate],
  );

  const onOpenSelfManaged = React.useCallback(
    (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
      const id = event.currentTarget.dataset.id;
      if (id === 'affiliates') navigate(ROUTE.affiliates());
      else if (id === 'influencers') navigate(ROUTE.influencers());
      else console.log('oh.module.acquisition[openSelfManaged]', id);
    },
    [navigate],
  );

  const onOpenCampaign = React.useCallback(
    (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
      const id = event.currentTarget.dataset.id;
      if (id) navigate(ROUTE.campaignDetail(id));
    },
    [navigate],
  );

  // Under the reveal the hero scores the Lumio levers and its shortcuts land in the Lumio sections ("Increase daily
  // budget" opens the card's Budget and Limits dialog, "Activate a new campaign" the browser's Suggested tab).
  // Without it the hero scores the affiliate program, headed by its next step, with one action opening Affiliates
  // (offered only while the Affiliates page is available).
  const affiliateScore = affiliateProgress.score;
  const hero = showLumioSections
    ? {
        ...data.insights.summary,
        heading: data.insights.summary.title,
        actions: data.insights.recommendations.map((r) => ({ id: r.id, label: r.label })),
        loading: insights.loading || !budgetsReady,
      }
    : {
        score: affiliateScore.score,
        status: affiliateScore.status,
        progress: affiliateScore.score,
        heading: affiliateScore.heading,
        actions:
          affiliateScore.nextStep && isChannelEnabled('affiliates')
            ? [{ id: 'open-affiliates', label: 'Open Affiliates' }]
            : [],
        loading: affiliateProgress.scoreLoading,
      };
  // The Newton showcase shows no scores, so the score hero is left out and the page starts at the channels.
  const newtonShowcase = useAppSelector(selectNShowcase);

  return (
    <PageShell styleText={styleText} className={className}>
      <AcqPage>
        {newtonShowcase ? null : (
          <Hero
            score={hero.score}
            status={hero.status}
            progress={hero.progress}
            heading={hero.heading}
            actions={hero.actions}
            onAction={onHeroAction}
            loading={hero.loading}
          />
        )}
        <SelfManaged channels={selfManagedChannels} onOpen={onOpenSelfManaged} />

        {showLumioSections ? (
          <>
            <ChannelsBudgets
              channels={lumioChannels.channels}
              budgets={budgets}
              budgetsLoading={!budgetsReady}
              onOpenChannel={onOpenChannel}
              onOpenBudgetLimits={onOpenBudgetLimits}
              onCloseBudgetLimits={onCloseBudgetLimits}
              openBudgetDialog={openBudgetDialog}
              loading={lumioChannels.loading}
            />
            <Campaigns
              ref={campaignsRef}
              tabs={data.campaigns.tabs}
              filters={data.campaigns.filters}
              pool={campaigns.pool}
              onOpenCampaign={onOpenCampaign}
            />
          </>
        ) : null}
        <Performance
          kpis={performance.kpis}
          range={perfRange}
          onRangeChange={setPerfRange}
          leftLabel={perfLabels.leftLabel}
          leftCaption={perfLabels.leftCaption}
          rightLabel={perfLabels.rightLabel}
          rightCaption={perfLabels.rightCaption}
          loading={performance.loading}
        />
      </AcqPage>
    </PageShell>
  );
};

export default Acquisition;
